import React, { useEffect, useRef, useState } from "react";
import {
  SERVER_MODE_KEY,
  SERVER_MODES,
  getServerMode,
} from "../config/connect";

/**
 * Floating indicator + switch between DEV (backend-test, agroland_test) and
 * PROD (backend, agroland_dev) backends.
 *
 * Choice is persisted in localStorage; switching reloads the page so every API
 * module picks up the new URL on next module-load.
 *
 * ТӨМЕНГІ СОЛ ЖАҚТА тұрады, жоғарғы оң жақта емес: оң жақ төбеде беттердің
 * негізгі батырмалары (мыс. оператор әңгімесіндегі «Чатты жабу») орналасқан,
 * ал бұл виджет `position: fixed` болғандықтан соларды жауып тұратын.
 *
 * `inline` — layout-тың тақырып жолағына кіріктірілген нұсқа (админ,
 * модератор): қалқымалы түрі сайдбардың төменгі «Выйти» батырмасын жабатын.
 */
const switchServer = (target) => {
  localStorage.setItem(SERVER_MODE_KEY, target.value);
  // Token from one server doesn't necessarily work on the other —
  // safest to clear so user re-signs in for the new backend.
  localStorage.removeItem("google_id_token");
  window.location.reload();
};

/**
 * Тақырып жолағына арналған сегментті ауыстырғыш: [● DEV | PROD].
 * Белсенді емес жаққа басқанда растау терезесі ашылады (қайта кіру керек екені айтылады).
 */
const InlineServerModeToggle = () => {
  const mode = getServerMode();
  const [pending, setPending] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!pending) return undefined;
    const onDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setPending(null);
    };
    const onKey = (e) => e.key === "Escape" && setPending(null);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pending]);

  const segments = [
    { cfg: SERVER_MODES.DEV, short: "DEV", activeCls: "bg-emerald-500/15 text-emerald-500 ring-1 ring-emerald-500/40", dot: "bg-emerald-500" },
    { cfg: SERVER_MODES.PROD, short: "PROD", activeCls: "bg-rose-500/15 text-rose-500 ring-1 ring-rose-500/40", dot: "bg-rose-500" },
  ];
  const toProd = pending?.value === "prod";

  return (
    <div ref={rootRef} className="relative">
      <div
        role="radiogroup"
        aria-label="Сервер"
        className="flex h-9 items-center gap-0.5 rounded-lg border border-border-primary bg-bg-primary p-0.5"
      >
        {segments.map(({ cfg, short, activeCls, dot }) => {
          const active = cfg.value === mode;
          return (
            <button
              key={cfg.value}
              type="button"
              role="radio"
              aria-checked={active}
              title={active ? `Текущий сервер: ${cfg.url}` : `Переключиться на ${cfg.label}`}
              onClick={() => !active && setPending(cfg)}
              className={`flex h-full items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold tracking-wide transition-colors ${
                active ? activeCls : "text-text-muted hover:bg-bg-tertiary hover:text-text-primary"
              }`}
            >
              {active && (
                <span className="relative flex h-2 w-2">
                  <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${dot}`} />
                  <span className={`relative inline-flex h-2 w-2 rounded-full ${dot}`} />
                </span>
              )}
              {short}
              {cfg.value === "dev" && <span className="hidden font-normal opacity-70 sm:inline">test</span>}
            </button>
          );
        })}
      </div>

      {pending && (
        <div
          role="dialog"
          aria-label={`Переключиться на ${pending.label}`}
          className="absolute right-0 top-full z-50 mt-2 w-72 rounded-block border border-border-primary bg-bg-card p-4 text-left shadow-lg"
        >
          <div className={`text-sm font-semibold ${toProd ? "text-rose-500" : "text-emerald-500"}`}>
            Переключиться на {pending.label}?
          </div>
          <p className="mt-1 text-xs text-text-secondary">
            {toProd
              ? "Все изменения будут применяться к живым данным пользователей."
              : "Панель переключится на тестовую базу."}{" "}
            Страница перезагрузится, потребуется войти заново.
          </p>
          <p className="mt-2 truncate font-mono text-[11px] text-text-muted" title={pending.url}>
            {pending.url.replace(/^https?:\/\//, "")}
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              autoFocus
              onClick={() => setPending(null)}
              className="flex-1 rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => switchServer(pending)}
              className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 ${
                toProd ? "bg-rose-600" : "bg-emerald-600"
              }`}
            >
              Переключить
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ServerModeToggle = ({ inline = false }) => {
  const [mode] = useState(getServerMode());
  const [confirming, setConfirming] = useState(false);

  if (inline) return <InlineServerModeToggle />;

  const cur = mode === "dev" ? SERVER_MODES.DEV : SERVER_MODES.PROD;
  const other = mode === "dev" ? SERVER_MODES.PROD : SERVER_MODES.DEV;

  const handleSwitch = () => {
    if (!confirming) {
      setConfirming(true);
      // Auto-clear confirmation after 4s if user does nothing
      setTimeout(() => setConfirming(false), 4000);
      return;
    }
    switchServer(other);
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: 8,
        left: 8,
        zIndex: 9999,
        boxShadow: "0 4px 16px rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        gap: 6,
        background: "rgba(17, 17, 17, 0.85)",
        border: `2px solid ${cur.color}`,
        borderRadius: 8,
        padding: "4px 10px",
        fontSize: 12,
        color: "#fff",
        fontFamily: "system-ui, sans-serif",
        backdropFilter: "blur(4px)",
        userSelect: "none",
      }}
      title={cur.url}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: cur.color,
          boxShadow: `0 0 8px ${cur.color}`,
        }}
      />
      <span style={{ fontWeight: 700, color: cur.color }}>{cur.label}</span>
      <button
        onClick={handleSwitch}
        style={{
          marginLeft: 6,
          padding: "2px 8px",
          borderRadius: 4,
          border: `1px solid ${other.color}`,
          background: confirming ? other.color : "transparent",
          color: confirming ? "#fff" : other.color,
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
          transition: "background 120ms",
        }}
        title={`Switch to ${other.label} (${other.url})`}
      >
        {confirming ? `Подтвердить → ${other.label}` : `→ ${other.label}`}
      </button>
    </div>
  );
};

export default ServerModeToggle;
